import { getAuth, onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/config";
import { initializeApp } from "firebase/app";
import { jwtDecode } from "jwt-decode"; // KAD Dec 29, 2024

let refreshingToken = null; // Global lock to prevent multiple refresh calls

// Helper function to check if the token is expired
function isTokenExpired(token) {
  try {
    if (typeof token !== "string") return false;

    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const earlyExpiryTime = decodedToken.exp - 600; // For refreshig forcefully 1200 seconds (20 min) from now
    return earlyExpiryTime < currentTime;
  } catch (error) {
    console.error("Error decoding token:", error);
    return true;
  }
}

// Helper function to refresh the token
async function refreshToken() {
  try {
    // It avoids crashing by returning null if auth.currentUser is not available. Vik Feb 6, 2025
    if (!auth?.currentUser) {
      console.warn("refreshToken: User is not authenticated.");
      return null; // Return null instead of crashing
    }
    const newToken = await auth.currentUser.getIdToken(true); // Force refresh
    localStorage.setItem("token", newToken);
    return newToken;
  } catch (error) {
    console.error("Error refreshing token:", error);
    return null;
  }
}

async function refreshTokenWithAuthState() {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        const newToken = await refreshToken();
        if (newToken) {
          localStorage.setItem("token", newToken);
        }
        resolve(newToken);
      } else {
        resolve(null);
      }
    });
  });
}

export async function fetchSecure(url, options) {
  let token = localStorage.getItem("token");
  const wixToken = localStorage.getItem("wixToken");

  // KAD trying to refresh tokens
  if (isTokenExpired(token)) {
    if (!refreshingToken) {
      refreshingToken = refreshTokenWithAuthState(); // Only first request triggers refresh
    }
    token = await refreshingToken; // Other requests wait for this to complete
    refreshingToken = null; // Reset lock after refresh
    if (token) {
      localStorage.setItem("token", token);
    }
  }

  // 🚀 Fetch the latest token again to prevent race conditions
  token = localStorage.getItem("token");

  if (options === undefined) {
    options = {
      headers: {
        Authorization: `Bearer ${token}`,
        Wixauthorization: `Bearer ${wixToken}`,
      },
    };
  } else {
    if (!("headers" in options)) {
      options["headers"] = {};
      options.headers = {
        Authorization: `Bearer ${token}`,
        Wixauthorization: `Bearer ${wixToken}`,
      };
    } else {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        Wixauthorization: `Bearer ${wixToken}`,
      };
    }
  }
  return fetch(url, options);
}

/* KAD is this used???? I don't think so Feb 8, 2025 TODO KAD FIX */
export function initializeFirebase(environmentName) {
  // Import the functions you need from the SDKs you need
  // TODO: Add SDKs for Firebase products that you want to use
  // https://firebase.google.com/docs/web/setup#available-libraries

  var firebaseConfig = "";

  const firebaseConfigDev = {
    apiKey: process.env.REACT_APP_FIREBASE_DEV_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_DEV_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_DEV_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_DEV_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_DEV_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_DEV_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_DEV_MEASUREMENT_ID,
  };
  const firebaseConfigProd = {
    apiKey: process.env.REACT_APP_FIREBASE_PROD_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_PROD_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROD_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_PROD_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_PROD_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_PROD_APP_ID,
  };
  const firebaseConfigTest = {
    apiKey: process.env.REACT_APP_FIREBASE_TEST_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_TEST_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_TEST_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_TEST_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_TEST_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_TEST_APP_ID,
  };

  if (environmentName === "Development") {
    firebaseConfig = firebaseConfigDev;
  }
  if (environmentName === "Production") {
    firebaseConfig = firebaseConfigProd;
  }
  if (environmentName === "Test") {
    firebaseConfig = firebaseConfigTest;
  }
  /*
firebaseConfig = firebaseConfigDev;
*/

  // Initialize Firebase
  const app = initializeApp(firebaseConfig);
  // Initialize Firebase Authentication and get a reference to the service
  return (auth = getAuth(app));
}

/* this function is used to make a camel case database field name like adminConfig and turn it into something we can display like Admin Config */

export function convertCamelToDisplayName(str) {
  // Handle undefined or non-string inputs
  if (str === undefined || str === null || typeof str !== "string") {
    return "N/A"; // Default fallback
  }

  // Check if the string is already in display format
  if (isDisplayFormat(str)) {
    return str;
  }
  /* Vik(May 30, 2025): Split on spaces and process each part */
  return str
    .split(" ")
    .map((part) => {
      // Handle all-uppercase with optional underscores (e.g., "DOCUMENT_QA")
      if (part.match(/^[A-Z]+(_[A-Z]+)*$/)) {
        return part.replace(/_/g, " ");
      }
      // Handle uppercase prefix followed by underscore-separated lowercase (e.g., "MMKK_hotel_service")
      const upperPrefixMatch = part.match(/^([A-Z]+)(_.+)$/);
      if (upperPrefixMatch) {
        const [, prefix, rest] = upperPrefixMatch;
        return prefix + " " + capitalizeWords(camelToNormal(rest.slice(1)));
      }
      // Handle parts with colons or other separators (e.g., "SAY:")
      if (part.includes(":")) {
        return part
          .split(":")
          .map((subPart) =>
            subPart.trim() ? capitalizeWords(camelToNormal(subPart.trim())) : ""
          )
          .join(":");
      }
      // Handle other cases (camelCase or underscore-separated)
      return capitalizeWords(camelToNormal(part));
    })
    .join(" ");
}

function camelToNormal(str) {
  return str
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/(?!^)([A-Z][a-z]+)/g, " $1") // Add space before capital letter followed by lowercase, except at start
    .toLowerCase();
}

export function capitalizeWords(str) {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function isDisplayFormat(str) {
  /* Vik(May 23, 2025): Check if string is empty or contains only spaces */
  if (!str || /^\s*$/.test(str)) {
    return true;
  }
  /* Vik(May 30, 2025): Check if all words are capitalized, all-uppercase, or valid punctuation (e.g., ":") */
  const words = str.split(" ");
  return (
    words.every(
      (word) =>
        word.length > 0 && // Non-empty word
        ((word.charAt(0) === word.charAt(0).toUpperCase() && // First letter capitalized
          word.slice(1) === word.slice(1).toLowerCase()) || // Rest lowercase
          word.match(/^[A-Z]+$/) || // Or all uppercase (e.g., "SAY", "ACT")
          word === ":") // Allow colons as valid
    ) && str.match(/^[\w:]+(\s[\w:]+)*$/)
  ); // Matches words or colons separated by single spaces
}

export async function logUserAction(userId, action, additionalInfo) {
  const dataToSend = {
    userId: userId,
    action: action,
    additionalInfo: additionalInfo,
  };
  const response = await fetchSecure("/api/logUserAction", {
    headers: { "Content-Type": "application/json" },
    method: "post",
    body: JSON.stringify(dataToSend),
  });
  await response.json();
}

// /*🔹 API to update agents with defaultFAQPrompt. Vik Feb 21, 2025 */
export async function updateAgents() {
  try {
    const response = await fetchSecure("/api/updateAgents", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    const data = await response.json();
    //console.log("response data :", data);
  } catch (error) {
    console.error("Error:", error);
  }
}

export function sendChatMessage(message) {
  const webhookURL =
    "https://chat.googleapis.com/v1/spaces/AAAAsBLekL4/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=VQZZMDn_x76U0Z7MFTH7lLfJhdLQJnJsnbMpuwH1txk";

  const data = JSON.stringify({
    text: message,
  });

  fetch(webhookURL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
    },
    body: data,
  }).then((response) => {
    //console.log(response);
  });
}
  /* KAD added Sept 30, 2025 to poll backend until success FOR CREATE BLOG ..but also other things */
        /**
         * Repeatedly calls an API endpoint until a success condition is met or a maximum timeout is reached.
         * * @param {string} apiUrl The URL of the API to poll.
         * @param {number} maxTimeMs Maximum time in milliseconds to continue polling (default 20000ms).
         * @param {number} intervalMs Time in milliseconds between polling attempts (default 5000ms).
         * @returns {Promise<object>} A Promise that resolves with the final API data on success or rejects on timeout.
         */
        export function pollUntilSuccess(apiUrl, maxTimeMs = 60000, intervalMs = 10000) {
            return new Promise((resolve, reject) => {
                const startTime = Date.now();

                const attemptPoll = async () => {
                    const elapsed = Date.now() - startTime;
                    const remainingTime = maxTimeMs - elapsed;

                    // 1. Check for Timeout Condition
                    if (elapsed >= maxTimeMs) {
                        const errorMsg = `🚨 Polling timed out. API did not 'turn' within ${maxTimeMs / 1000}s.`;
                        console.log(errorMsg, 'error');
                        return reject(new Error(errorMsg));
                    }

                    console.log(`Attempting fetch... (Time elapsed: ${Math.round(elapsed / 1000)}s / Max: ${maxTimeMs / 1000}s)`);

                    try {
                        // In a real application, you would perform a simple fetch here:
                        // const response = await fetch(apiUrl);
                        
                        // --- START MOCK LOGIC ---
                        // Using a public API for structure, but simulating delayed success for testing
                        //const response = await fetchSecure(apiUrl);
                        const response = await fetchSecure(apiUrl, {
                          method: "GET",
                          headers: { "Content-Type": "application/json" }
                        });
                        const data = await response.json();

                        // In a real app, this would be a check like: data.status === 'READY' or data.job_finished === true
                        const hasTurned = elapsed >= 10000;
                        // --- END MOCK LOGIC ---

                        // 2. Check for Success Condition
                        if (response.ok && hasTurned && "generatedContent" in data && data["generatedContent"]) {
                            console.log(`✅ Success! API has 'turned'.`, 'success');
                            //console.log(`Final Data received: ${JSON.stringify(data, null, 2)}`);
                            return resolve(data);
                        } else {
                            // 3. Continue Polling
                            const waitMsg = `⏳ Status not yet 'turned'. Retrying in ${intervalMs / 1000} seconds...`;
                            console.log(waitMsg, 'wait');
                            setTimeout(attemptPoll, intervalMs);
                        }
                    } catch (error) {
                        // 4. Handle Errors but Continue Polling
                        //customLog(`🚨 Attempt failed (Network/API error): ${error.message}`, 'error');
                        //customLog(`⏳ Retrying in ${intervalMs / 1000} seconds due to error...`, 'wait');
                        setTimeout(attemptPoll, intervalMs);
                    }
                };

                // Start the first check immediately
                attemptPoll();
            });
        }

// module.exports = { fetchSecure };
